if (!customElements.get('tab-spot-xray')) {
  customElements.define(
    'tab-spot-xray',
    class TabSpotXray extends HTMLElement {
      connectedCallback() {
        this.payload = this.parsePayload();
        this.shell = this.querySelector('[data-tab-xray-shell]');
        this.content = this.querySelector('[data-tab-xray-content]');
        this.empty = this.querySelector('[data-tab-xray-empty]');
        this.spotLabel = this.querySelector('[data-tab-xray-spot]');
        this.sets = this.querySelector('[data-tab-xray-sets]');
        this.stats = this.querySelector('[data-tab-xray-stats]');
        this.people = this.querySelector('[data-tab-xray-people]');
        this.peopleTitle = this.querySelector('[data-tab-xray-people-title]');
        this.showMore = this.querySelector('[data-tab-xray-show-more]');
        this.initialNames = Number.parseInt(this.dataset.initialNames || '8', 10);
        this.expanded = false;

        if (!this.payload?.components?.length || !this.shell) {
          this.remove();
          return;
        }

        this.showMore?.addEventListener('click', () => {
          this.expanded = !this.expanded;
          this.updateExtraNames();
        });

        this.render(this.payload.initialVariant);
        this.shell.hidden = false;
        this.dataset.state = 'ready';

        if (
          typeof subscribe === 'function' &&
          typeof PUB_SUB_EVENTS !== 'undefined' &&
          PUB_SUB_EVENTS.variantChange
        ) {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (String(data?.sectionId) !== String(this.dataset.sectionId)) return;
            const optionIndex = Number(this.payload.spotOptionIndex || 0);
            const selectedSpot = data?.variant?.options?.[optionIndex] || data?.variant?.title;
            if (selectedSpot) this.render(selectedSpot);
          });
        }
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
      }

      parsePayload() {
        try {
          return JSON.parse(this.querySelector('[data-tab-xray-payload]')?.textContent || '{}');
        } catch (error) {
          console.error('TAB Spot X-Ray: Daten konnten nicht gelesen werden.', error);
          return null;
        }
      }

      cleanLabel(value) {
        return String(value || '')
          .normalize('NFKC')
          .replace(/[–—−]/g, '-')
          .replace(/\s+/g, ' ')
          .trim();
      }

      keyForExactLabel(value) {
        const compact = this.cleanLabel(value)
          .toUpperCase()
          .replace(/\s*\/\s*/g, '/')
          .replace(/\s*-\s*/g, '-')
          .replace(/\s+/g, ' ');

        const aliases = {
          A: 'A',
          'B-WOMEN': 'B_WOMEN',
          'B WOMEN': 'B_WOMEN',
          'B-FRAUEN': 'B_WOMEN',
          'B-MEN': 'B_MEN',
          'B MEN': 'B_MEN',
          'B-MÄNNER': 'B_MEN',
          C: 'C',
          D: 'D',
          'E/F': 'E_F',
          EF: 'E_F',
          G: 'G',
          H: 'H',
          I: 'I',
          'J-WOMEN': 'J_WOMEN',
          'J WOMEN': 'J_WOMEN',
          'J-FRAUEN': 'J_WOMEN',
          'J-MEN': 'J_MEN',
          'J MEN': 'J_MEN',
          'J-MÄNNER': 'J_MEN',
          K: 'K',
          L: 'L',
          M: 'M',
          N: 'N',
          O: 'O',
          P: 'P',
          'R-WOMEN': 'R_WOMEN',
          'R WOMEN': 'R_WOMEN',
          'R-FRAUEN': 'R_WOMEN',
          'R-MEN': 'R_MEN',
          'R MEN': 'R_MEN',
          'R-MÄNNER': 'R_MEN',
          'S-WOMEN': 'S_WOMEN',
          'S WOMEN': 'S_WOMEN',
          'S-FRAUEN': 'S_WOMEN',
          'S-MEN': 'S_MEN',
          'S MEN': 'S_MEN',
          'S-MÄNNER': 'S_MEN',
          'T-WOMEN': 'T_WOMEN',
          'T WOMEN': 'T_WOMEN',
          'T-FRAUEN': 'T_WOMEN',
          'T-MEN': 'T_MEN',
          'T MEN': 'T_MEN',
          'T-MÄNNER': 'T_MEN',
          THE: 'THE',
          U: 'U',
          'V-Z': 'V_Z',
          'V/Z': 'V_Z',
          'V BIS Z': 'V_Z',
        };

        return aliases[compact] || null;
      }

      selectionQuery(value) {
        const label = this.cleanLabel(value);
        const exactKey = this.keyForExactLabel(label);
        if (exactKey) return { label, exactKey, exactLabel: null, routes: null, gender: null };

        const upper = label.toUpperCase();
        const gender = /WOMEN|FRAUEN/.test(upper) ? 'women' : /MEN|MÄNNER/.test(upper) ? 'men' : null;
        const routeSource = upper
          .replace(/WOMEN|FRAUEN|MEN|MÄNNER/g, '')
          .replace(/[()]/g, ' ')
          .trim();
        const routes = new Set();

        routeSource.split(/\s*(?:\+|&|,)\s*/).forEach((part) => {
          const range = part.match(/^([A-Z])-([A-Z])$/);
          if (range) {
            const start = range[1].charCodeAt(0);
            const end = range[2].charCodeAt(0);
            for (let code = Math.min(start, end); code <= Math.max(start, end); code += 1) {
              routes.add(String.fromCharCode(code));
            }
            return;
          }

          part.split(/\s*\/\s*/).forEach((token) => {
            const cleaned = token.replace(/[^A-Z]/g, '');
            if (cleaned === 'THE') routes.add('THE');
            else if (cleaned.length === 1) routes.add(cleaned);
          });
        });

        return { label, exactKey: null, exactLabel: upper, routes, gender };
      }

      entryMatches(query, spot, person) {
        if (query.exactKey) return spot.key === query.exactKey;
        if (query.exactLabel && this.cleanLabel(spot.label).toUpperCase() === query.exactLabel) return true;
        if (!query.routes?.has(String(person.route || '').toUpperCase())) return false;
        return !query.gender || person.gender === query.gender;
      }

      render(selectedSpot) {
        const query = this.selectionQuery(selectedSpot);
        const mergedPeople = new Map();
        const totals = { base: 0, inserts: 0, autos: 0, relics: 0 };

        this.payload.components.forEach((component) => {
          const spots = component?.data?.spots || [];
          spots.forEach((spot) => {
            (spot.people || []).forEach((person) => {
              if (!this.entryMatches(query, spot, person)) return;

              totals.base += Number(person.base || 0);
              totals.inserts += Number(person.inserts || 0);
              totals.autos += Number(person.autos || 0);
              totals.relics += Number(person.relics || 0);

              const identity = this.cleanLabel(person.name).toLocaleLowerCase('de');
              if (!mergedPeople.has(identity)) {
                mergedPeople.set(identity, {
                  name: person.name,
                  legend: Boolean(person.legend),
                  rookie: Boolean(person.rookie),
                  entries: [],
                });
              }

              const merged = mergedPeople.get(identity);
              merged.legend ||= Boolean(person.legend);
              merged.rookie ||= Boolean(person.rookie);
              merged.entries.push({ component, person });
            });
          });
        });

        const people = Array.from(mergedPeople.values()).sort((a, b) =>
          a.name.localeCompare(b.name, 'de', { sensitivity: 'base' })
        );

        this.expanded = false;
        this.spotLabel.textContent = query.label || selectedSpot;
        this.renderSets();
        this.renderStats(people.length, totals);
        this.renderPeople(people);

        const hasPeople = people.length > 0;
        this.empty.hidden = hasPeople;
        this.content.hidden = !hasPeople;
      }

      renderSets() {
        this.sets.replaceChildren();
        this.payload.components.forEach((component) => {
          const pill = document.createElement('span');
          pill.className = 'tab-xray__set-pill';
          const count = Number(component.boxCount || 1);
          pill.textContent = `${count}× ${component.name || component.fullName || 'Set'}`;
          this.sets.appendChild(pill);
        });
      }

      renderStats(nameCount, totals) {
        const items = [
          ['Namen', nameCount],
          ['Base', totals.base],
          ['Inserts', totals.inserts],
          ['Autos', totals.autos],
          ['Relics', totals.relics],
        ];

        this.stats.replaceChildren(
          ...items.map(([label, value]) => {
            const item = document.createElement('div');
            item.className = 'tab-xray__stat';

            const number = document.createElement('strong');
            number.textContent = new Intl.NumberFormat('de-DE').format(value);
            const caption = document.createElement('span');
            caption.textContent = label;
            item.append(number, caption);
            return item;
          })
        );
      }

      renderPeople(people) {
        this.people.replaceChildren();
        this.peopleTitle.textContent = `${people.length} ${people.length === 1 ? 'Name' : 'Namen'} in diesem Spot`;

        people.forEach((person, index) => {
          const details = document.createElement('details');
          details.className = 'tab-xray-person';
          if (index >= this.initialNames) details.classList.add('tab-xray-person--extra');

          const summary = document.createElement('summary');
          const identity = document.createElement('span');
          identity.className = 'tab-xray-person__identity';
          const name = document.createElement('strong');
          name.textContent = person.name;
          identity.appendChild(name);

          if (person.rookie) identity.appendChild(this.badge('RC', 'rookie'));
          if (person.legend) identity.appendChild(this.badge('LEGEND', 'legend'));

          const overview = document.createElement('span');
          overview.className = 'tab-xray-person__overview';
          const setNames = [...new Set(person.entries.map(({ component }) => component.name || component.fullName))];
          overview.textContent = setNames.join(' · ');

          const caret = document.createElement('span');
          caret.className = 'tab-xray-person__caret';
          caret.setAttribute('aria-hidden', 'true');
          summary.append(identity, overview, caret);

          const body = document.createElement('div');
          body.className = 'tab-xray-person__body';
          person.entries.forEach((entry) => body.appendChild(this.renderPersonEntry(entry)));

          details.append(summary, body);
          this.people.appendChild(details);
        });

        const hiddenCount = Math.max(0, people.length - this.initialNames);
        this.showMore.hidden = hiddenCount === 0;
        this.showMore.dataset.hiddenCount = String(hiddenCount);
        this.updateExtraNames();
      }

      renderPersonEntry({ component, person }) {
        const entry = document.createElement('section');
        entry.className = 'tab-xray-entry';

        const heading = document.createElement('h4');
        const count = Number(component.boxCount || 1);
        heading.textContent = `${component.fullName || component.name} · ${count} ${count === 1 ? 'Box' : 'Boxen'}`;
        entry.appendChild(heading);

        const chips = document.createElement('div');
        chips.className = 'tab-xray-entry__chips';
        [
          ['Base', person.base],
          ['Inserts', person.inserts],
          ['Autos', person.standardAutos],
          ['Relic-Autos', person.relicAutos],
          ['Relics', person.relics],
        ].forEach(([label, value]) => {
          if (!Number(value || 0)) return;
          const chip = document.createElement('span');
          chip.textContent = `${label} ×${value}`;
          chips.appendChild(chip);
        });
        entry.appendChild(chips);

        this.appendSetLine(entry, 'Insert-Sets', person.insertSets);
        this.appendSetLine(entry, 'Auto-Sets', person.autoSets);
        this.appendSetLine(entry, 'Relic-Auto-Sets', person.relicAutoSets);
        this.appendSetLine(entry, 'Relic-Sets', person.relicSets);

        if (Number(person.multiAutos || 0) > 0) {
          const note = document.createElement('p');
          note.className = 'tab-xray-entry__multi';
          note.textContent = `${person.multiAutos} mögliche Multi-Auto-Zuordnung – bei verschiedenen Spots gilt die Break-Regel.`;
          entry.appendChild(note);
        }

        return entry;
      }

      appendSetLine(parent, label, values) {
        if (!Array.isArray(values) || values.length === 0) return;
        const line = document.createElement('p');
        const strong = document.createElement('strong');
        strong.textContent = `${label}: `;
        line.append(strong, document.createTextNode(values.join(', ')));
        parent.appendChild(line);
      }

      badge(label, type) {
        const badge = document.createElement('span');
        badge.className = `tab-xray-person__badge tab-xray-person__badge--${type}`;
        badge.textContent = label;
        return badge;
      }

      updateExtraNames() {
        this.people.querySelectorAll('.tab-xray-person--extra').forEach((item) => {
          item.hidden = !this.expanded;
        });

        const hiddenCount = Number(this.showMore?.dataset.hiddenCount || 0);
        if (!this.showMore || hiddenCount === 0) return;
        this.showMore.textContent = this.expanded ? 'Weniger Namen anzeigen' : `Alle Namen anzeigen (+${hiddenCount})`;
        this.showMore.setAttribute('aria-expanded', String(this.expanded));
      }
    }
  );
}
